import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CoachDashboardData, CoachAthlete } from '@/types/coach';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { log } from '@/lib/logger';

const EMPTY_DASHBOARD_DATA: CoachDashboardData = {
  adherence_pct: 0,
  adherence_trend: [],
  load_trend: 'stable',
  risk_level: 'low',
  risks: [],
  weekly_loads: [],
};

/**
 * Hook to fetch coach's assigned athletes.
 * GET /api/coach/athletes. In preview mode returns empty array (no mock data).
 */
export function useCoachAthletes() {
  const { status } = useAuth();
  const isAuthenticated = status === 'authenticated';

  return useQuery({
    queryKey: ['coach', 'athletes'],
    queryFn: async (): Promise<CoachAthlete[]> => {
      const response = await api.get('/coach/athletes');
      
      // Backend may return { athletes: [...] } or just [...]
      if (response && typeof response === 'object' && 'athletes' in response) {
        const athletesArray = (response as { athletes: unknown[] }).athletes;
        if (Array.isArray(athletesArray)) {
          return athletesArray.map((athlete: unknown) => {
            const a = athlete as Record<string, unknown>;
            // Map backend format to frontend format
            return {
              id: String(a.id || ''),
              name: String(a.name || ''),
              email: typeof a.email === 'string' ? a.email : undefined,
              avatar_url: typeof a.avatar_url === 'string' ? a.avatar_url : undefined,
              avatar_initials: typeof (a as { avatar_initials?: string }).avatar_initials === 'string'
                ? (a as { avatar_initials: string }).avatar_initials
                : undefined,
              last_activity_date: typeof a.last_activity_date === 'string' 
                ? a.last_activity_date 
                : undefined,
            } as CoachAthlete;
          });
        }
      }
      
      // If response is directly an array
      if (Array.isArray(response)) {
        return response.map((athlete: unknown) => {
          const a = athlete as Record<string, unknown>;
          return {
            id: String(a.id || ''),
            name: String(a.name || ''),
            email: typeof a.email === 'string' ? a.email : undefined,
            avatar_url: typeof a.avatar_url === 'string' ? a.avatar_url : undefined,
            avatar_initials: typeof (a as { avatar_initials?: string }).avatar_initials === 'string'
              ? (a as { avatar_initials: string }).avatar_initials
              : undefined,
            last_activity_date: typeof a.last_activity_date === 'string' 
              ? a.last_activity_date 
              : undefined,
          } as CoachAthlete;
        });
      }
      
      // Fallback: return empty array if response format is unexpected
      log.warn('COACH', 'Unexpected response format:', response);
      return [];
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to access coach dashboard data for a specific athlete.
 * GET /api/coach/athletes/{athlete_id}/dashboard.
 * Returns dataUnavailable: true when in preview or no athlete or API failure (show empty state).
 */
export function useCoachDashboardData(athleteId?: string): CoachDashboardData & { isLoading?: boolean; error?: unknown; dataUnavailable?: boolean } {
  const { status } = useAuth();
  const isAuthenticated = status === 'authenticated';

  const query = useQuery({
    queryKey: ['coach', 'dashboard', athleteId],
    queryFn: async (): Promise<CoachDashboardData> => {
      if (!athleteId) {
        throw new Error("Data unavailable");
      }

      const response = await api.get(`/coach/athletes/${athleteId}/dashboard`);
      
      // Backend response should match CoachDashboardData interface
      const dashboardData = response as unknown as CoachDashboardData;
      
      // Validate required fields exist
      if (!dashboardData || typeof dashboardData !== 'object') {
        throw new Error('Invalid dashboard data response from API');
      }

      return {
        adherence_pct: typeof dashboardData.adherence_pct === 'number' 
          ? dashboardData.adherence_pct 
          : 0,
        adherence_trend: Array.isArray(dashboardData.adherence_trend)
          ? dashboardData.adherence_trend
          : [],
        load_trend: dashboardData.load_trend || 'stable',
        risk_level: dashboardData.risk_level || 'low',
        risks: Array.isArray(dashboardData.risks) ? dashboardData.risks : [],
        weekly_loads: Array.isArray(dashboardData.weekly_loads) 
          ? dashboardData.weekly_loads 
          : [],
      };
    },
    enabled: isAuthenticated && !!athleteId,
    staleTime: 2 * 60 * 1000, // 2 minutes - dashboard data changes more frequently
    retry: 1, // Only retry once on failure
  });

  if (!athleteId) {
    return {
      ...EMPTY_DASHBOARD_DATA,
      isLoading: false,
      dataUnavailable: true,
    };
  }

  if (query.isLoading || !query.data) {
    return {
      ...EMPTY_DASHBOARD_DATA,
      isLoading: true,
      dataUnavailable: false,
    };
  }

  if (query.error) {
    return {
      ...EMPTY_DASHBOARD_DATA,
      isLoading: false,
      error: query.error,
      dataUnavailable: true,
    };
  }

  return {
    ...query.data,
    isLoading: false,
    dataUnavailable: false,
  };
}

/**
 * Hook to manage athlete selection state
 * Returns the selected athlete and a setter function
 */
export function useAthleteSelection() {
  const { data: athletes, isLoading } = useCoachAthletes();
  const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(null);

  // Auto-select first athlete when data loads
  useEffect(() => {
    if (athletes && athletes.length > 0 && !selectedAthleteId) {
      setSelectedAthleteId(athletes[0].id);
    }
  }, [athletes, selectedAthleteId]);

  const selectedAthlete = athletes?.find(a => a.id === selectedAthleteId) ?? null;

  return {
    athletes: athletes ?? [],
    selectedAthlete,
    selectedAthleteId,
    setSelectedAthleteId,
    isLoading,
  };
}
